import { type ApiAddress } from '@Model';
import { Autocomplete, useTranslate, type IGuiBinder } from '@synqo/frontend-component-library';
import { CityNoticeArea } from './CityNoticeArea';
import classes from './LocalityFields.module.css';
import { type LocalityAutomation } from './useLocalityAutomation';

// @ai-generated 2026-09-17T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

export interface AddressCityColumnProps {
    binder: IGuiBinder<ApiAddress>;
    automation: LocalityAutomation;
    /** Shown below the field while the automation has nothing of its own to say. */
    hint?: string;
}

/**
 * The city field and everything the automation says about it. The field stays free text: the
 * localities of the postal code are offered in its suggestion list, which opens whenever the cursor
 * arrives — including when the automation puts it there, having found a postal code with more than
 * one place to it.
 *
 * The list is the one place a choice is ever made, so nothing below the field repeats it. What is
 * left there is a line saying how the field and the postal code stand to one another, and the two
 * cases the list cannot carry: a single locality, which gets no list at all, and a contradiction,
 * where keeping what is in the field has to be an answer of its own.
 */
export function AddressCityColumn({ binder, automation, hint }: AddressCityColumnProps) {
    const i18n = useTranslate('');
    const { lookup, offered, notice, cityInputRef } = automation;
    const { acceptLocality, noteLocalityPicked, keepCity, noteCityTyping, noteCityBlurred } = automation;

    const cityProps = binder.bindToProp<string>('city');

    return (
        <div className={classes.fieldWithNotice}>
            <Autocomplete
                {...cityProps}
                onChange={(value, valid) => {
                    noteCityTyping(value);
                    return cityProps.onChange(value, valid);
                }}
                onPick={noteLocalityPicked}
                onBlur={noteCityBlurred}
                inputRef={cityInputRef}
                suggestions={offered}
                // Reported in the field, so that typing a postal code does not leave the city
                // field looking as though it had nothing to offer.
                loading={lookup.status === 'searching'}
                listLabel={`${i18n('Orte zu')} ${lookup.zip}`}
                label={i18n('Stadt')}
            />
            <CityNoticeArea notice={notice} hint={hint} onAccept={acceptLocality} onKeep={keepCity} />
        </div>
    );
}
